import React, { useState } from 'react';
import Header from './Header';
import Footer from './Footer';
import { searchFlights } from './services/flightApiService';
import './FlightSearchTestPage.css';

const FlightSearchTestPage = () => {
  const [searchParams, setSearchParams] = useState({
    origin: 'LOS',
    destination: 'LHR',
    departureDate: '',
    returnDate: '',
    adults: 1,
    children: 0,
    infants: 0,
    cabinClass: 'ECONOMY'
  });

  const [loading, setLoading] = useState(false);
  const [flights, setFlights] = useState([]);
  const [error, setError] = useState(null);
  const [searchExecuted, setSearchExecuted] = useState(false);

  React.useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dateStr = tomorrow.toISOString().split('T')[0];
    setSearchParams(prev => ({
      ...prev,
      departureDate: dateStr
    }));
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setFlights([]);
    setSearchExecuted(false);

    try {
      const result = await searchFlights(searchParams);
      
      if (result.success) {
        setFlights(result.data);
        setSearchExecuted(true);
      } else {
        setError(result.error || 'Failed to search flights');
        console.error('Search error:', result);
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
      console.error('Search exception:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="flight-search-test-page">
      <Header />
      
      <div className="test-page-container">
        <div className="test-page-header">
          <h1>Flight search test (Duffel)</h1>
          <p>Uses the same flight search API as the main app (Supabase Edge Function or local backend)</p>
        </div>

        <div className="test-search-form">
          <form onSubmit={handleSearch}>
            <div className="form-row">
              <div className="form-group">
                <label>From (Airport Code)</label>
                <input
                  type="text"
                  name="origin"
                  value={searchParams.origin}
                  onChange={handleInputChange}
                  placeholder="e.g., LOS"
                  required
                  maxLength="3"
                  style={{ textTransform: 'uppercase' }}
                />
              </div>

              <div className="form-group">
                <label>To (Airport Code)</label>
                <input
                  type="text"
                  name="destination"
                  value={searchParams.destination}
                  onChange={handleInputChange}
                  placeholder="e.g., LHR"
                  required
                  maxLength="3"
                  style={{ textTransform: 'uppercase' }}
                />
              </div>

              <div className="form-group">
                <label>Departure Date</label>
                <input
                  type="date"
                  name="departureDate"
                  value={searchParams.departureDate}
                  onChange={handleInputChange}
                  required
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div className="form-group">
                <label>Return Date (Optional)</label>
                <input
                  type="date"
                  name="returnDate"
                  value={searchParams.returnDate}
                  onChange={handleInputChange}
                  min={searchParams.departureDate || new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Adults</label>
                <input
                  type="number"
                  name="adults"
                  value={searchParams.adults}
                  onChange={handleInputChange}
                  min="1"
                  max="9"
                />
              </div>

              <div className="form-group">
                <label>Children (2-11)</label>
                <input
                  type="number"
                  name="children"
                  value={searchParams.children}
                  onChange={handleInputChange}
                  min="0"
                  max="9"
                />
              </div>

              <div className="form-group">
                <label>Infants (0-2)</label>
                <input
                  type="number"
                  name="infants"
                  value={searchParams.infants}
                  onChange={handleInputChange}
                  min="0"
                  max="9"
                />
              </div>

              <div className="form-group">
                <label>Cabin Class</label>
                <select
                  name="cabinClass"
                  value={searchParams.cabinClass}
                  onChange={handleInputChange}
                >
                  <option value="ECONOMY">Economy</option>
                  <option value="PREMIUM_ECONOMY">Premium Economy</option>
                  <option value="BUSINESS">Business</option>
                  <option value="FIRST">First</option>
                </select>
              </div>
            </div>

            <button 
              type="submit" 
              className="search-button"
              disabled={loading}
            >
              {loading ? 'Searching...' : 'Search Flights'}
            </button>
          </form>
        </div>

        {error && (
          <div className="error-message">
            <h3>Error</h3>
            <p>{error}</p>
            <details>
              <summary>Technical Details</summary>
              <pre>{JSON.stringify(error, null, 2)}</pre>
            </details>
          </div>
        )}

        {loading && (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Searching for flights...</p>
          </div>
        )}

        {searchExecuted && !loading && (
          <div className="results-section">
            <h2>
              {flights.length > 0 
                ? `Found ${flights.length} Flight${flights.length !== 1 ? 's' : ''}`
                : 'No Flights Found'
              }
            </h2>

            {flights.length > 0 ? (
              <div className="flights-list">
                {flights.map((flight, index) => (
                  <div key={flight.id || index} className="flight-card">
                    <div className="flight-header">
                      <div className="flight-airline">
                        <span className="airline-logo">✈️</span>
                        <span className="airline-name">{flight.airline}</span>
                        <span className="flight-number">{flight.flightNumber}</span>
                      </div>
                      <div className="flight-price">
                        {formatPrice(flight.price.total)}
                      </div>
                    </div>

                    <div className="flight-route">
                      <div className="route-segment">
                        <div className="route-time">
                          <span className="time">{flight.departureTime}</span>
                          <span className="airport">{flight.departureAirport}</span>
                        </div>
                        <div className="route-info">
                          <span className="duration">{flight.durationFormatted}</span>
                          <div className="route-line">
                            <span className="dot"></span>
                            <span className="line"></span>
                            <span className="dot"></span>
                          </div>
                          <span className="stops">
                            {flight.stops === 0 ? 'Non-stop' : `${flight.stops} Stop${flight.stops !== 1 ? 's' : ''}`}
                          </span>
                        </div>
                        <div className="route-time">
                          <span className="time">{flight.arrivalTime}</span>
                          <span className="airport">{flight.arrivalAirport}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flight-details">
                      <div className="detail-item">
                        <strong>Base Fare:</strong> {formatPrice(flight.price.base)}
                      </div>
                      <div className="detail-item">
                        <strong>Taxes:</strong> {formatPrice(flight.price.taxes)}
                      </div>
                      <div className="detail-item">
                        <strong>Available Seats:</strong> {flight.numberOfBookableSeats || 'N/A'}
                      </div>
                      <div className="detail-item">
                        <strong>Validating Airline:</strong> {flight.validatingAirlineCodes?.join(', ') || 'N/A'}
                      </div>
                    </div>

                    {flight.outbound.segments.length > 1 && (
                      <div className="flight-segments">
                        <strong>Segments:</strong>
                        {flight.outbound.segments.map((segment, idx) => (
                          <div key={idx} className="segment">
                            {segment.departure.airport} → {segment.arrival.airport}
                            {' '}
                            ({segment.carrier}{segment.flightNumber})
                          </div>
                        ))}
                      </div>
                    )}

                    {flight.return && (
                      <div className="return-flight">
                        <strong>Return Flight:</strong>
                        <div className="route-segment">
                          <div className="route-time">
                            <span className="time">
                              {new Date(flight.return.segments[0].departure.time).toLocaleTimeString('en-US', { 
                                hour: '2-digit', 
                                minute: '2-digit',
                                hour12: false 
                              })}
                            </span>
                            <span className="airport">{flight.return.segments[0].departure.airport}</span>
                          </div>
                          <div className="route-info">
                            <span className="duration">{flight.return.duration}</span>
                          </div>
                          <div className="route-time">
                            <span className="time">
                              {new Date(flight.return.segments[flight.return.segments.length - 1].arrival.time).toLocaleTimeString('en-US', { 
                                hour: '2-digit', 
                                minute: '2-digit',
                                hour12: false 
                              })}
                            </span>
                            <span className="airport">{flight.return.segments[flight.return.segments.length - 1].arrival.airport}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="flight-raw-data">
                      <details>
                        <summary>Raw API Data</summary>
                        <pre>{JSON.stringify(flight, null, 2)}</pre>
                      </details>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-flights">
                <p>No flights found for your search criteria. Try different dates or airports.</p>
              </div>
            )}
          </div>
        )}

        <div className="info-section">
          <h3>Test Information</h3>
          <ul>
            <li><strong>API:</strong> Duffel (via Supabase Edge Function or local backend)</li>
            <li><strong>Status:</strong> {searchExecuted ? (flights.length > 0 ? '✅ Working' : '⚠️ No Results') : 'Ready to test'}</li>
            <li><strong>Note:</strong> This page mirrors production flight search; credentials stay on the server.</li>
          </ul>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FlightSearchTestPage;
